import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Pause, Play } from 'lucide-react'

interface Props {
  url: string
  label: string
}

const WAVEFORM_HEIGHTS = [
  4, 12, 18, 10, 16, 22, 14, 8, 12, 19, 15, 6, 11, 14, 10, 5, 12, 18, 14, 7,
  5, 12, 18, 14, 7, 5, 12, 18, 14, 7, 9, 16, 11, 20, 8, 13, 6, 17, 12, 5,
  15, 9, 19, 7, 13, 18, 10, 5, 12, 20, 14, 8, 16, 11, 6, 18, 13, 9, 21, 7,
  14, 10, 17, 5, 12, 19, 8, 15, 11, 6, 18, 13, 9, 16, 7, 12, 20, 10, 5, 14,
] as const

export function GrowthAlertAudioPlayer({ url, label }: Props) {
  const { t } = useTranslation('coaching')
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)

  const togglePlayback = async () => {
    const audio = audioRef.current
    if (!audio) return

    if (audio.paused) {
      try {
        await audio.play()
      } catch {
        setIsPlaying(false)
      }
    } else {
      audio.pause()
    }
  }

  const seek = (nextTime: number) => {
    const audio = audioRef.current
    if (!audio || duration <= 0) return
    audio.currentTime = nextTime
    setCurrentTime(nextTime)
  }

  const progress = duration > 0 ? currentTime / duration : 0

  return (
    <div
      className="flex h-12 w-full items-center gap-3 rounded-2xl bg-[#f4f4f4] pl-3 pr-4 md:h-14"
      data-testid="growth-alert-audio-player"
    >
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onLoadedMetadata={(event) => setDuration(event.currentTarget.duration || 0)}
        onDurationChange={(event) => setDuration(event.currentTarget.duration || 0)}
        onTimeUpdate={(event) => setCurrentTime(event.currentTarget.currentTime)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => {
          setIsPlaying(false)
          setCurrentTime(0)
        }}
      />

      <button
        type="button"
        onClick={togglePlayback}
        aria-label={isPlaying
          ? t('growthAlert.audioPause', { label })
          : t('growthAlert.audioPlay', { label })}
        className="flex size-[22px] shrink-0 items-center justify-center rounded-[11px] bg-[#4599ff] text-white transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4599ff]"
      >
        {isPlaying ? (
          <Pause className="size-3 fill-current" aria-hidden="true" />
        ) : (
          <Play className="ml-0.5 size-[11px] fill-current" aria-hidden="true" />
        )}
      </button>

      <div className="relative min-w-0 flex-1">
        <WaveformBars progress={progress} className="md:hidden" barCount={40} />
        <WaveformBars progress={progress} className="hidden md:flex" barCount={80} />
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={Math.min(currentTime, duration || 0)}
          onChange={(event) => seek(Number(event.target.value))}
          aria-label={t('growthAlert.audioSeek', { label })}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
        />
      </div>

      <span className="shrink-0 text-xs font-bold tabular-nums text-[#4599ff] md:text-sm">
        {formatAudioTime(duration)}
      </span>
    </div>
  )
}

function WaveformBars({
  progress,
  className,
  barCount,
}: {
  progress: number
  className: string
  barCount: number
}) {
  return (
    <div className={`h-6 items-center justify-between gap-0.5 overflow-hidden ${className}`}>
      {WAVEFORM_HEIGHTS.slice(0, barCount).map((height, index) => (
        <span
          key={`${height}-${index}`}
          className="w-[3px] shrink-0 rounded-[1.5px] transition-colors duration-100"
          style={{
            height,
            backgroundColor: (index + 1) / barCount <= progress ? '#4599ff' : '#cccccc',
          }}
        />
      ))}
    </div>
  )
}

function formatAudioTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return '00:00'
  const rounded = Math.round(seconds)
  const minutes = Math.floor(rounded / 60)
  const remainingSeconds = rounded % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}
